import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Controller } from "react-hook-form";
import PropTypes from "prop-types";

import useNewUserInfoColumns from "./hooks/useNewUserInfoColumns";
import useNewUserInfoForm from "./hooks/useNewUserInfoForm";
import useStyles from "./hooks/useNewUserInfoStyles";
import useUnsavedChangesWarning from "./hooks/useUnsavedChangesWarning";
import useGetRoles from "../../../hooks/useGetRoles";
import { AddUserContainer, ContainerVariant } from "../AddUserContainer";
import {
  Button,
  Checkbox,
  EmailInput,
  ErrorMessage,
  Icon,
  Input,
  PermissionDropdown,
  Table,
} from "./components";
import LoadCRMSuggestions from "./LoadCRMSuggestions";

function NewUserInfoPage({
  defaultNewUserInfo,
  loadingNotInvitedUser,
  onChange: updateNewUserInfo,
  toNext,
  containerVariant,
  cancelButton,
  addUserTitle,
  addUserDescription,
  addUserButtonText,
  hideInviteNowColumn,
  onUnsavedDataChange,
  onUnsavedDataErrorChange,
  onClose,
}) {
  const classes = useStyles();
  const newUserInfoColumns = useNewUserInfoColumns(hideInviteNowColumn);
  const { defaultRole, data: roleData, loading: roleLoading } = useGetRoles();
  const [isVerifying, setIsVerifying] = useState(false);
  const {
    control,
    setError,
    handleSubmit,
    isSubmitting,
    errors,
    clearErrors,
    fields,
    update,
    append,
    remove,
    getPrunedUserInfo,
  } = useNewUserInfoForm({
    defaultNewUserInfo: defaultNewUserInfo,
    defaultInviteNow: hideInviteNowColumn,
  });

  useUnsavedChangesWarning(
    control,
    useMemo(() => {
      return defaultRole;
    }, [defaultRole]),
    useCallback(onUnsavedDataChange, []),
    useCallback(onUnsavedDataErrorChange, [])
  );

  const [isAllowNext, setIsAllowNext] = useState(true);

  const setAllowToProceed = useCallback(() => {
    const validUserInfoRows = getPrunedUserInfo(defaultRole);
    setIsAllowNext(validUserInfoRows.length > 0);
  }, [defaultRole, getPrunedUserInfo, setIsAllowNext]);

  useEffect(() => {
    if (defaultNewUserInfo && defaultRole) {
      setAllowToProceed();
    }
  }, [defaultNewUserInfo, defaultRole, setAllowToProceed]);

  const getFieldError = (rowIndex, fieldName) => {
    return errors.newUserInfo && errors.newUserInfo[rowIndex]
      ? errors.newUserInfo[rowIndex][fieldName]
      : null;
  };

  const newUsers = fields.map((item, i) => {
    return {
      id: item.id,
      firstName: (
        <div className={classes.cell}>
          <Controller
            render={({ field: { onChange, ...restFields } }) => {
              return (
                <Input
                  {...restFields}
                  error={Boolean(getFieldError(i, "firstName"))}
                  className={classes.cell}
                  onChange={(e) => {
                    onChange(e);
                    setAllowToProceed();
                  }}
                />
              );
            }}
            name={`newUserInfo.${i}.firstName`}
            control={control}
          />
          {getFieldError(i, "firstName") && (
            <ErrorMessage message={errors.newUserInfo[i].firstName.message} />
          )}
        </div>
      ),
      lastName: (
        <div className={classes.cell}>
          <Controller
            render={({ field: { onChange, ...restField } }) => {
              return (
                <Input
                  {...restField}
                  error={Boolean(getFieldError(i, "lastName"))}
                  className={classes.cell}
                  onChange={(e) => {
                    onChange(e);
                    setAllowToProceed();
                  }}
                />
              );
            }}
            name={`newUserInfo.${i}.lastName`}
            control={control}
          />
          {getFieldError(i, "lastName") && (
            <ErrorMessage message={errors.newUserInfo[i].lastName.message} />
          )}
        </div>
      ),
      email: (
        <div className={classes.cell}>
          <Controller
            render={({ field: { ref, onChange, ...restFields } }) => {
              return (
                <EmailInput
                  {...restFields}
                  error={Boolean(getFieldError(i, "email"))}
                  onVerifying={setIsVerifying}
                  setError={setError}
                  clearErrors={clearErrors}
                  onChange={(e) => {
                    onChange(e);
                    setAllowToProceed();
                  }}
                />
              );
            }}
            name={`newUserInfo.${i}.email`}
            control={control}
          />
          {getFieldError(i, "email") && (
            <ErrorMessage message={errors.newUserInfo[i].email.message} />
          )}
        </div>
      ),
      userRole: (
        <Controller
          render={({ field: { value, onChange } }) => {
            return (
              <PermissionDropdown
                data-test="add-user-role-dropdown"
                disabled={roleLoading}
                value={value || defaultRole?.id}
                roleData={roleData}
                onChangeCb={onChange}
                style={{ margin: "18px 0" }}
              />
            );
          }}
          name={`newUserInfo.${i}.roleId`}
          control={control}
        />
      ),
      inviteNow: (
        <Controller
          render={({ field: { value, onChange, ...restFields } }) => {
            return (
              <Checkbox
                className={classes.cell}
                toggle
                checked={value}
                onChange={(_, { checked }) => {
                  onChange(checked);
                }}
                {...(hideInviteNowColumn ? { style: { display: "none" } } : {})}
                {...restFields}
              />
            );
          }}
          name={`newUserInfo.${i}.inviteNow`}
          control={control}
        />
      ),
      deleteRow: item.advisorId ? (
        <Icon className={classes.cell} size="large" name="" />
      ) : (
        <Icon
          className={classes.cell}
          style={{ cursor: "pointer" }}
          size="large"
          name="remove"
          onClick={() => {
            remove(i);
            setAllowToProceed();
          }}
        />
      ),
    };
  });

  return (
    <React.Fragment>
      <AddUserContainer.Header variant={containerVariant} onClose={onClose}>
        {addUserTitle}
      </AddUserContainer.Header>
      <AddUserContainer.Body
        variant={containerVariant}
        className={classes.modalBody}
      >
        <p>
          {addUserDescription}
          <LoadCRMSuggestions onSuggestions={update} />
        </p>
        <form
          id="addUserForm"
          className={classes.form}
          onSubmit={handleSubmit(() => {
            const prunedUserInfo = getPrunedUserInfo(defaultRole);
            if (prunedUserInfo.length === 0) {
              return;
            }
            updateNewUserInfo([...prunedUserInfo]);
            toNext();
          })}
        >
          <Table className={classes.table}>
            <Table.Content>
              <Table.Header columns={newUserInfoColumns} />
              {!loadingNotInvitedUser && newUsers.length > 0 && (
                <Table.Body
                  isLoading={false}
                  columns={newUserInfoColumns}
                  data={newUsers}
                />
              )}
            </Table.Content>
          </Table>
          <Button
            type="button"
            content={addUserButtonText}
            onClick={() => {
              return append({
                firstName: "",
                lastName: "",
                email: "",
                roleId: "",
                inviteNow: hideInviteNowColumn,
              });
            }}
          />
        </form>
      </AddUserContainer.Body>
      <AddUserContainer.Footer variant={containerVariant}>
        {cancelButton}
        <Button
          color="blue"
          content="Next"
          type="submit"
          form="addUserForm"
          disabled={
            isVerifying ||
            isSubmitting ||
            roleLoading ||
            loadingNotInvitedUser ||
            Boolean(errors?.newUserInfo?.length) ||
            !isAllowNext
          }
          loading={loadingNotInvitedUser || isVerifying || isSubmitting}
        />
      </AddUserContainer.Footer>
    </React.Fragment>
  );
}

NewUserInfoPage.propTypes = {
  defaultNewUserInfo: PropTypes.arrayOf(
    PropTypes.shape({
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      email: PropTypes.string,
      roleId: PropTypes.string,
      inviteNow: PropTypes.bool,
    })
  ),
  loadingNotInvitedUser: PropTypes.bool,
  onChange: PropTypes.func,
  onUnsavedDataChange: PropTypes.func,
  toNext: PropTypes.func,
  onClose: PropTypes.func,
  containerVariant: PropTypes.oneOf(Object.values(ContainerVariant)),
  cancelButton: PropTypes.node,
  addUserTitle: PropTypes.string,
  addUserDescription: PropTypes.string,
  addUserButtonText: PropTypes.string,
  hideInviteNowColumn: PropTypes.bool,
  onUnsavedDataErrorChange: PropTypes.func,
};

NewUserInfoPage.defaultProps = {
  defaultNewUserInfo: [
    {
      firstName: "",
      lastName: "",
      email: "",
      roleId: "",
      inviteNow: true,
    },
  ],
  loadingNotInvitedUser: false,
  onChange: () => {},
  onUnsavedDataChange: () => {},
  toNext: () => {},
  onClose: () => {},
  containerVariant: ContainerVariant.MODAL,
  cancelButton: undefined,
  addUserTitle: "Invite Users",
  addUserDescription: "Quickly add and invite members of your team.",
  addUserButtonText: "+ Add User",
  hideInviteNowColumn: false,
  onUnsavedDataErrorChange: () => {},
};

export default NewUserInfoPage;
